import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'

function DDPRepeaterItem ({ item, accessor, format, className, style, component, componentProps, ...rest }) {
  const { formatter } = useFormattingContext()

  const _item = useMemo(() => {
    let value = accessor ? get(item, accessor) : item
    if (value !== null && typeof value === 'object') {
      value = value.toString()
    }
    return format ? formatter(value, format) : value
  }, [item, accessor, format, formatter])

  if (component) {
    return React.cloneElement(component, {
      ...item,
      ...rest,
      ...(componentProps || {})
    })
  }

  return (
    <div className={className} style={style} {...rest}>
      {_item || null}
    </div>
  )
}

DDPRepeaterItem.propTypes = {
  item: PropTypes.any,
  accessor: PropTypes.string,
  format: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  component: PropTypes.node,
  componentProps: PropTypes.object
}

function DDPRepeater ({
  pipeline,
  variables,
  dataAccessor,
  itemAccessor,
  format,
  className,
  style,
  itemClassName,
  itemStyle,
  itemProps,
  component,
  componentProps,
  ...rest
}) {
  const { data, isLoading, error } = usePipelineDataContextWithVariables(pipeline, variables)

  const itemSeries = useMemo(() => {
    const series = get(data, dataAccessor)
    if ([null, undefined].includes(series)) return null
    if (Array.isArray(series)) {
      return series
    }
    if (series !== null && typeof series === 'object') {
      return [series.toString()]
    }
    return [series]
  }, [data, dataAccessor])

  if (isLoading || error) {
    return null
  }

  return (
    <div className={className} style={style} {...rest}>
      {(itemSeries || []).map((item, index) => (
        <DDPRepeaterItem
          key={`repeater_item_${index}`}
          accessor={itemAccessor}
          item={item}
          format={format}
          className={itemClassName}
          style={itemStyle}
          component={component}
          componentProps={componentProps}
          {...itemProps}
        />
      ))}
    </div>
  )
}

DDPRepeater.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  variables: PropTypes.any,

  /** Path to the array or item to display */
  dataAccessor: PropTypes.string,

  /** Path within array to item to display */
  itemAccessor: PropTypes.string,

  format: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  itemClassName: PropTypes.string,
  itemStyle: PropTypes.object,
  itemProps: PropTypes.object,
  component: PropTypes.node,
  componentProps: PropTypes.object
}

export default DDPRepeater
